import { AssignmentGroupingType } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import { DateTime } from 'luxon'
import { AssignmentType } from '@breakoutlearning/firebase-repository/types'
import type { useTranslationTyped } from 'i18n/i18n'
import { RoomState } from '@breakoutlearning/firebase-repository/models/RoomState'
import { z } from 'zod'
import { dateSchema } from 'util/schema-date'
import { type Section } from '@breakoutlearning/firebase-repository/models/Section'

export const getAssignmentSchema = ({
  tt,
  minExpiresAtOverride,
  sectionStartDate,
  sectionEndDate,
}: {
  tt: ReturnType<typeof useTranslationTyped>['tt']
  minExpiresAtOverride?: {
    date: DateTime
    message: string
  }
  sectionStartDate: Section['data']['sectionStartDate']
  sectionEndDate: Section['data']['sectionEndDate']
}) => {
  const minExpiresAtDate = minExpiresAtOverride
    ? minExpiresAtOverride.date
    : DateTime.now().minus({
        minutes: 1,
      })

  const minExpiresAtDateMessage =
    minExpiresAtOverride?.message ??
    tt.instructor_library.deadline_must_be_future()

  return z
    .object({
      assignmentType: z.preprocess((val) => {
        return Number(val)
      }, z.nativeEnum(AssignmentType)),
      sectionId: z
        .string({ message: tt.instructor_library.section_missing() })
        .min(1, tt.instructor_library.section_missing()),
      catalogId: z.string().optional(),
      assignedAt: dateSchema({
        required: tt.instructor_assignment.start_date_required(),
      }),
      expiresAt: dateSchema({
        required: tt.instructor_library.deadline_required(),
        min: {
          date: minExpiresAtDate,
          message: minExpiresAtDateMessage,
        },
      }),
      groupingType: z.preprocess((val) => {
        return Number(val)
      }, z.nativeEnum(AssignmentGroupingType)),
      groupingSize: z.coerce
        .number()
        .min(1, { message: tt.instructor_library.grouping_size_invalid() })
        .max(RoomState.maxAllowedUsers, {
          message: tt.instructor_library.grouping_size_invalid(),
        })
        .optional(),
      groupingSizeMinimum: z.coerce
        .number()
        .min(1, { message: tt.instructor_library.grouping_size_invalid() })
        .max(RoomState.maxAllowedUsers, {
          message: tt.instructor_library.grouping_size_invalid(),
        })
        .optional(),
      groupingSizeMaximum: z.coerce
        .number()
        .min(1, { message: tt.instructor_library.grouping_size_invalid() })
        .max(RoomState.maxAllowedUsers, {
          message: tt.instructor_library.grouping_size_invalid(),
        })
        .optional(),
      configureGrading: z.boolean(),
      customGroupingSelected: z.boolean().optional(),
      forbidStudentGroupCreation: z.boolean().optional(),
      forbidStudentGroupScheduling: z.boolean().optional(),
      forbidStudentGroupSelection: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
      const { assignedAt, expiresAt } = data

      if (assignedAt && expiresAt && expiresAt <= assignedAt) {
        return ctx.addIssue({
          path: ['expiresAt'],
          code: z.ZodIssueCode.custom,
          message: tt.instructor_assignment.due_date_must_be_later(),
        })
      }

      if (
        sectionStartDate &&
        assignedAt < DateTime.fromJSDate(sectionStartDate).startOf('hour')
      )
        ctx.addIssue({
          path: ['assignedAt'],
          code: z.ZodIssueCode.custom,
          message:
            tt.instructor_assignment.assigned_at_before_section_start_date({
              date: DateTime.fromJSDate(sectionStartDate).toLocaleString(
                DateTime.DATETIME_MED
              ),
            }),
        })

      if (sectionEndDate && expiresAt > DateTime.fromJSDate(sectionEndDate))
        ctx.addIssue({
          path: ['expiresAt'],
          code: z.ZodIssueCode.custom,
          message: tt.instructor_assignment.expires_at_after_section_end_date({
            date: DateTime.fromJSDate(sectionEndDate).toLocaleString(
              DateTime.DATETIME_MED
            ),
          }),
        })
    })
}

export const getDraftAssignmentSchema = ({
  tt,
  sectionStartDate,
  sectionEndDate,
}: {
  tt: ReturnType<typeof useTranslationTyped>['tt']
  sectionStartDate: Section['data']['sectionStartDate']
  sectionEndDate: Section['data']['sectionEndDate']
}) => {
  return z
    .object({
      assignmentType: z.preprocess((val) => {
        return Number(val)
      }, z.nativeEnum(AssignmentType)),
      sectionId: z
        .string({ message: tt.instructor_library.section_missing() })
        .min(1, tt.instructor_library.section_missing()),
      catalogId: z.string().optional(),
      assignedAt: dateSchema({
        required: tt.instructor_assignment.start_date_required(),
      }).optional(),
      expiresAt: dateSchema({
        required: tt.instructor_library.deadline_required(),
        min: {
          date: DateTime.now().minus({
            minutes: 1,
          }),
          message: tt.instructor_library.due_date_must_be_future(),
        },
      }).optional(),
      groupingType: z.preprocess((val) => {
        return Number(val)
      }, z.nativeEnum(AssignmentGroupingType)),
      groupingSize: z.coerce
        .number()
        .min(1, { message: tt.instructor_library.grouping_size_invalid() })
        .max(RoomState.maxAllowedUsers, {
          message: tt.instructor_library.grouping_size_invalid(),
        })
        .optional(),
      groupingSizeMinimum: z.coerce
        .number()
        .min(1, { message: tt.instructor_library.grouping_size_invalid() })
        .max(RoomState.maxAllowedUsers, {
          message: tt.instructor_library.grouping_size_invalid(),
        })
        .optional(),
      groupingSizeMaximum: z.coerce
        .number()
        .min(1, { message: tt.instructor_library.grouping_size_invalid() })
        .max(RoomState.maxAllowedUsers, {
          message: tt.instructor_library.grouping_size_invalid(),
        })
        .optional(),
      configureGrading: z.boolean(),
      forbidStudentGroupCreation: z.boolean().optional(),
      forbidStudentGroupScheduling: z.boolean().optional(),
      forbidStudentGroupSelection: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
      const { assignedAt, expiresAt } = data

      if (assignedAt && expiresAt && expiresAt <= assignedAt) {
        return ctx.addIssue({
          path: ['expiresAt'],
          code: z.ZodIssueCode.custom,
          message: tt.instructor_assignment.due_date_must_be_later(),
        })
      }

      if (
        sectionStartDate &&
        assignedAt &&
        assignedAt < DateTime.fromJSDate(sectionStartDate).startOf('hour')
      )
        ctx.addIssue({
          path: ['assignedAt'],
          code: z.ZodIssueCode.custom,
          message:
            tt.instructor_assignment.assigned_at_before_section_start_date({
              date: DateTime.fromJSDate(sectionStartDate).toLocaleString(
                DateTime.DATETIME_MED
              ),
            }),
        })

      if (
        sectionEndDate &&
        expiresAt &&
        expiresAt > DateTime.fromJSDate(sectionEndDate)
      )
        ctx.addIssue({
          path: ['expiresAt'],
          code: z.ZodIssueCode.custom,
          message: tt.instructor_assignment.expires_at_after_section_end_date({
            date: DateTime.fromJSDate(sectionEndDate).toLocaleString(
              DateTime.DATETIME_MED
            ),
          }),
        })
    })
}

export const getLtiAssignmentSchema = ({
  tt,
}: {
  tt: ReturnType<typeof useTranslationTyped>['tt']
}) => {
  return z.object({
    catalogId: z.string().optional(),
    configureGrading: z.boolean(),
    customGroupingSelected: z.boolean().optional(),
    forbidStudentGroupCreation: z.boolean().optional(),
    forbidStudentGroupScheduling: z.boolean().optional(),
    forbidStudentGroupSelection: z.boolean().optional(),
    groupingType: z.preprocess((val) => {
      return Number(val)
    }, z.nativeEnum(AssignmentGroupingType)),
    groupingSize: z.coerce
      .number()
      .min(1, { message: tt.instructor_library.grouping_size_invalid() })
      .max(RoomState.maxAllowedUsers, {
        message: tt.instructor_library.grouping_size_invalid(),
      })
      .optional(),
    groupingSizeMinimum: z.coerce
      .number()
      .min(1, { message: tt.instructor_library.grouping_size_invalid() })
      .max(RoomState.maxAllowedUsers, {
        message: tt.instructor_library.grouping_size_invalid(),
      })
      .optional(),
    groupingSizeMaximum: z.coerce
      .number()
      .min(1, { message: tt.instructor_library.grouping_size_invalid() })
      .max(RoomState.maxAllowedUsers, {
        message: tt.instructor_library.grouping_size_invalid(),
      })
      .optional(),
  })
}

export type AssignmentFormValues = z.infer<
  ReturnType<typeof getAssignmentSchema>
>
export type DraftAssignmentFormValues = z.infer<
  ReturnType<typeof getDraftAssignmentSchema>
>
export type LtiAssignmentFormValues = z.infer<
  ReturnType<typeof getLtiAssignmentSchema>
>
